import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { CAMPAIGN_COUNT_FIELD, CAMPAIGN_STATUS_FIELD, CAMPAIGN_DELIVERY_STATUS_QUERY } from "./query";
/**
 * Typed handler for the Campaign Delivery Status widget: campaign counts bucketed
 * by delivery status (Active / Paused / Completed) plus an overall completion
 * rate. Modelled separately from the generic breakdown handler because it is not
 * a metric-by-dimension breakdown. Fetched via a typed handler rather than a raw
 * frontend query envelope.
 */
export default class LookerOverviewCampaignDeliveryStatus extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-campaign-delivery-status");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...CAMPAIGN_DELIVERY_STATUS_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        const mediaType = mediaTypeFilter?.value;
        if (mediaType && mediaType.length) {
            queryBody.filters["explore_overview.product_group"] = Array.isArray(mediaType)
                ? mediaType.join(",")
                : mediaType;
        }
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (advertiserIds?.length) {
            queryBody.filters["meta_inst.advertiser_id"] = advertiserIds.join(",");
        }
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (campaignIds?.length) {
            queryBody.filters["meta_inst.campaign_id"] = campaignIds.join(",");
        }
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: [this.formatResponse(data)],
                queryMeta
            }
        ])
            .catch((e) => {
            throw Object.assign(new Error("Failed to fetch overview dashboard campaign delivery status"), { cause: e });
        }));
    }
    formatResponse(data) {
        // Looker returns Active / Paused / Completed / Archived. The widget shows the
        // first three (per the design); Archived is intentionally excluded. Statuses
        // are matched exactly (case-insensitive) so lookalikes such as "Inactive" do
        // not fall into the Active bucket.
        let active = 0;
        let paused = 0;
        let completed = 0;
        for (const row of data) {
            const status = String(row[CAMPAIGN_STATUS_FIELD] ?? "").toLowerCase();
            const count = Number(row[CAMPAIGN_COUNT_FIELD]) || 0;
            if (status === "active")
                active += count;
            else if (status === "paused")
                paused += count;
            else if (status === "completed")
                completed += count;
        }
        const totalCampaigns = active + paused + completed;
        // Overall completion % is derived as completed / total until a dedicated
        // Looker measure is provided; isolated here so the source can change without
        // touching the widget.
        const completionRate = totalCampaigns > 0 ? Math.round((completed / totalCampaigns) * 100) : 0;
        return { active, paused, completed, totalCampaigns, completionRate };
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=CampaignDeliveryStatus.js.map